import React, { useEffect, useMemo, useState } from "react";
import { Pagination } from "flowbite-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
  AlertTriangle,
  BookOpen,
  CheckSquare,
  Layers,
  Link2,
  Loader2,
  Package,
  PauseCircle,
  Plus,
  RefreshCw,
  Search,
  Square,
  Unlink,
  X,
} from "lucide-react";
import AdminLayout, { StatCard } from "./AdminLayout";
import { apiGet, apiPut, errorMessage, toastOpts } from "./adminApi";
import { getUserRole } from "../../utils/auth";
import { AreaBadge, ProductPill, QualificationCell, loadProducts, paginationTheme } from "./qualificationShared";

const PAGE_SIZE = 15;

/**
 * Product ↔ Qualification management: pick a product, review the qualifications
 * attached to it, detach some, or attach more from the qualification catalogue.
 */
export default function ProductQualificationsPage() {
  const queryClient = useQueryClient();
  const isAdmin = getUserRole() === "Admin";

  const [productId, setProductId] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selected, setSelected] = useState(() => new Set());
  const [assignOpen, setAssignOpen] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [searchTerm]);

  const { data: products = [], isLoading: loadingProducts } = useQuery({
    queryKey: ["adminProducts"],
    queryFn: loadProducts,
    staleTime: 5 * 60 * 1000,
  });

  // Pick the first product automatically
  useEffect(() => {
    if (!productId && products.length) setProductId(String(products[0].id));
  }, [products, productId]);

  const product = useMemo(() => products.find((p) => String(p.id) === String(productId)), [products, productId]);

  const params = useMemo(() => {
    const p = { page: currentPage - 1, size: PAGE_SIZE };
    if (debouncedSearch) p.search = debouncedSearch;
    return p;
  }, [currentPage, debouncedSearch]);

  const { data: page, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ["productQualifications", productId, params],
    queryFn: () => apiGet(`hec/products/${productId}/qualifications`, params),
    enabled: Boolean(productId),
    keepPreviousData: true,
    staleTime: 30 * 1000,
    retry: 1,
  });

  const rows = page?.content || [];
  const totalPages = page?.totalPages || 1;
  const totalElements = page?.totalElements || 0;

  // Total attached (independent of the search box)
  const { data: attachedTotal } = useQuery({
    queryKey: ["productQualificationsTotal", productId],
    queryFn: async () => (await apiGet(`hec/products/${productId}/qualifications`, { page: 0, size: 1 }))?.totalElements || 0,
    enabled: Boolean(productId),
    staleTime: 30 * 1000,
    retry: 1,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["productQualifications"] });
    queryClient.invalidateQueries({ queryKey: ["productQualificationsTotal"] });
    queryClient.invalidateQueries({ queryKey: ["adminQualifications"] });
    queryClient.invalidateQueries({ queryKey: ["adminQualificationsTotals"] });
  };

  const toggle = (id) =>
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const allOnPageSelected = rows.length > 0 && rows.every((r) => selected.has(r.id));
  const togglePage = () =>
    setSelected((s) => {
      const n = new Set(s);
      if (allOnPageSelected) rows.forEach((r) => n.delete(r.id));
      else rows.forEach((r) => n.add(r.id));
      return n;
    });

  const unassign = async (ids) => {
    if (!ids.length) return;
    setIsRemoving(true);
    try {
      const res = await apiPut(`hec/products/${productId}/qualifications/unassign`, { qualificationIds: ids });
      toast.success(res?.message || "Qualification(s) removed from product.", toastOpts);
      setSelected(new Set());
      invalidate();
    } catch (err) {
      toast.error(errorMessage(err), toastOpts);
    } finally {
      setIsRemoving(false);
    }
  };

  const handleProductChange = (value) => {
    setProductId(value);
    setCurrentPage(1);
    setSelected(new Set());
  };

  return (
    <AdminLayout
      title="Product Qualifications"
      subtitle="Attach qualifications to a product so applicants in those programmes are covered by it"
      icon={Layers}
      actions={
        <>
          <button
            onClick={() => refetch()}
            disabled={!productId}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-cyan-700 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${isFetching ? "animate-spin" : ""}`} />
            Refresh
          </button>
          {isAdmin && (
            <button
              onClick={() => setAssignOpen(true)}
              disabled={!productId}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-cyan-600 rounded-lg hover:bg-cyan-700 transition-colors shadow-sm disabled:opacity-50"
            >
              <Plus className="w-4 h-4" />
              Attach Qualifications
            </button>
          )}
        </>
      }
    >
      {/* Product picker + summary */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 lg:col-span-1">
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Product</label>
          {loadingProducts ? (
            <div className="flex items-center gap-2 text-sm text-gray-400 py-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading products...
            </div>
          ) : products.length === 0 ? (
            <p className="text-sm text-gray-500">No products yet. Create one under Administration → Products.</p>
          ) : (
            <select
              value={productId}
              onChange={(e) => handleProductChange(e.target.value)}
              className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none bg-white"
            >
              {products.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.productName}
                  {p.isOnhold ? " (on hold)" : ""}
                </option>
              ))}
            </select>
          )}
          {product && (
            <div className="mt-4 space-y-2 text-sm">
              <div className="flex items-start gap-2">
                <Package className="w-4 h-4 text-cyan-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-gray-900">{product.productName}</p>
                  <p className="text-xs text-gray-500">{product.organization || "—"}</p>
                </div>
              </div>
              {product.productDescription && <p className="text-xs text-gray-500 leading-relaxed">{product.productDescription}</p>}
              {product.isOnhold && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
                  <PauseCircle className="w-3 h-3" /> On hold
                </span>
              )}
            </div>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 lg:col-span-2">
          <StatCard icon={BookOpen} value={productId ? attachedTotal ?? "…" : "—"} label="Qualifications attached" />
          <StatCard icon={Search} value={productId ? totalElements : "—"} label="Matching search" tone="gray" />
          <StatCard icon={CheckSquare} value={selected.size} label="Selected for removal" tone="amber" />
          <StatCard icon={Package} value={products.length} label="Products" tone="gray" />
        </div>
      </div>

      {/* Search + bulk actions */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search attached qualifications by code, name or institution..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              disabled={!productId}
              className="w-full pl-9 pr-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none disabled:bg-gray-50"
            />
          </div>
          {isAdmin && selected.size > 0 && (
            <button
              onClick={() => unassign(Array.from(selected))}
              disabled={isRemoving}
              className="flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors disabled:opacity-50"
            >
              {isRemoving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Unlink className="w-4 h-4" />}
              Remove {selected.size} selected
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {!productId ? (
          <div className="flex flex-col items-center justify-center py-24 text-gray-400">
            <Package className="w-10 h-10 mb-3" />
            <p className="text-sm font-medium">Select a product to manage its qualifications</p>
          </div>
        ) : isLoading ? (
          <div className="flex flex-col items-center justify-center py-24 text-gray-400">
            <Loader2 className="w-10 h-10 animate-spin text-cyan-500 mb-3" />
            <p className="text-sm">Loading qualifications...</p>
          </div>
        ) : isError ? (
          <div className="flex flex-col items-center justify-center py-24 text-gray-400">
            <AlertTriangle className="w-10 h-10 text-red-400 mb-3" />
            <p className="text-sm font-medium text-red-600">Failed to load qualifications</p>
            <p className="text-xs mt-1 text-gray-500">{errorMessage(error)}</p>
            <button onClick={() => refetch()} className="mt-3 text-sm text-cyan-600 hover:underline">
              Try again
            </button>
          </div>
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 text-gray-400">
            <BookOpen className="w-10 h-10 mb-3" />
            <p className="text-sm font-medium">
              {debouncedSearch ? "No attached qualifications match your search" : "No qualifications attached to this product yet"}
            </p>
            {isAdmin && !debouncedSearch && (
              <button onClick={() => setAssignOpen(true)} className="mt-4 flex items-center gap-2 text-sm text-cyan-600 hover:underline">
                <Plus className="w-4 h-4" /> Attach qualifications
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    {isAdmin && (
                      <th className="px-5 py-3.5 w-10">
                        <button onClick={togglePage} className="text-gray-400 hover:text-cyan-600" title="Select page">
                          {allOnPageSelected ? <CheckSquare className="w-4 h-4 text-cyan-600" /> : <Square className="w-4 h-4" />}
                        </button>
                      </th>
                    )}
                    <th className="px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">#</th>
                    <th className="px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Qualification</th>
                    <th className="px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Institution</th>
                    <th className="px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Study Area</th>
                    <th className="px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Duration</th>
                    {isAdmin && <th className="px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide text-right">Actions</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {rows.map((q, index) => (
                    <tr key={q.id} className={`hover:bg-gray-50 transition-colors ${selected.has(q.id) ? "bg-cyan-50/40" : ""}`}>
                      {isAdmin && (
                        <td className="px-5 py-4">
                          <button onClick={() => toggle(q.id)} className="text-gray-400 hover:text-cyan-600">
                            {selected.has(q.id) ? <CheckSquare className="w-4 h-4 text-cyan-600" /> : <Square className="w-4 h-4" />}
                          </button>
                        </td>
                      )}
                      <td className="px-5 py-4 text-gray-400 text-xs font-mono">{(currentPage - 1) * PAGE_SIZE + index + 1}</td>
                      <td className="px-5 py-4 max-w-md">
                        <QualificationCell q={q} />
                      </td>
                      <td className="px-5 py-4 text-gray-600">{q.institution || <span className="text-gray-400">—</span>}</td>
                      <td className="px-5 py-4">
                        <div className="flex flex-col gap-1">
                          <span className="text-gray-700">{q.studyAreaId?.studyAreaName || "—"}</span>
                          <AreaBadge area={q.studyArea} />
                        </div>
                      </td>
                      <td className="px-5 py-4 text-gray-600">{q.programDuration || <span className="text-gray-400">—</span>}</td>
                      {isAdmin && (
                        <td className="px-5 py-4">
                          <div className="flex justify-end">
                            <button
                              onClick={() => unassign([q.id])}
                              disabled={isRemoving}
                              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors disabled:opacity-50"
                            >
                              <Unlink className="w-3.5 h-3.5" /> Remove
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-between px-5 py-4 border-t border-gray-100 gap-3">
              <p className="text-sm text-gray-500">
                Showing{" "}
                <span className="font-medium text-gray-700">
                  {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, totalElements)}
                </span>{" "}
                of <span className="font-medium text-gray-700">{totalElements}</span> qualifications
              </p>
              {totalPages > 1 && (
                <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={(p) => setCurrentPage(p)} showIcons theme={paginationTheme} />
              )}
            </div>
          </>
        )}
      </div>

      {assignOpen && product && (
        <AssignQualificationsModal
          product={product}
          onClose={() => setAssignOpen(false)}
          onAssigned={() => {
            setAssignOpen(false);
            invalidate();
          }}
        />
      )}
    </AdminLayout>
  );
}

// ── Attach qualifications modal ─────────────────────────────────────────────
function AssignQualificationsModal({ product, onClose, onAssigned }) {
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [scope, setScope] = useState("unassigned"); // 'unassigned' | 'all'
  const [currentPage, setCurrentPage] = useState(1);
  const [picked, setPicked] = useState(() => new Map()); // id -> qualification
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const params = useMemo(() => {
    const p = { page: currentPage - 1, size: 10 };
    if (debouncedSearch) p.search = debouncedSearch;
    if (scope === "unassigned") p.unassigned = true;
    return p;
  }, [currentPage, debouncedSearch, scope]);

  const { data: page, isLoading, isError, error } = useQuery({
    queryKey: ["assignableQualifications", params],
    queryFn: () => apiGet("hec/qualification/admin/all", params),
    keepPreviousData: true,
    retry: 1,
  });
  const rows = page?.content || [];
  const totalPages = page?.totalPages || 1;
  const totalElements = page?.totalElements || 0;

  const togglePick = (q) =>
    setPicked((m) => {
      const n = new Map(m);
      if (n.has(q.id)) n.delete(q.id);
      else n.set(q.id, q);
      return n;
    });

  const submit = async () => {
    if (!picked.size) {
      toast.warning("Select at least one qualification.", toastOpts);
      return;
    }
    setIsSubmitting(true);
    try {
      const res = await apiPut(`hec/products/${product.id}/qualifications/assign`, { qualificationIds: Array.from(picked.keys()) });
      toast.success(res?.message || "Qualifications attached.", toastOpts);
      onAssigned();
    } catch (err) {
      toast.error(errorMessage(err), toastOpts);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[92vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-cyan-50 to-white">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-cyan-100 flex items-center justify-center">
              <Link2 className="w-4 h-4 text-cyan-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">Attach Qualifications</h2>
              <p className="text-xs text-gray-500 mt-0.5">to {product.productName}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Controls */}
        <div className="px-6 pt-4 pb-3 flex flex-col sm:flex-row gap-3 border-b border-gray-100">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              autoFocus
              placeholder="Search qualifications by code, name or institution..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none"
            />
          </div>
          <select
            value={scope}
            onChange={(e) => {
              setScope(e.target.value);
              setCurrentPage(1);
            }}
            className="sm:w-64 px-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none bg-white"
          >
            <option value="unassigned">Unassigned qualifications only</option>
            <option value="all">All qualifications (incl. other products)</option>
          </select>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-16 text-gray-400">
              <Loader2 className="w-8 h-8 animate-spin text-cyan-500 mb-2" />
              <p className="text-sm">Loading...</p>
            </div>
          ) : isError ? (
            <div className="flex flex-col items-center justify-center py-16 text-gray-400">
              <AlertTriangle className="w-8 h-8 text-red-400 mb-2" />
              <p className="text-sm text-red-600">{errorMessage(error)}</p>
            </div>
          ) : rows.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-gray-400">
              <BookOpen className="w-8 h-8 mb-2" />
              <p className="text-sm">No qualifications found</p>
            </div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {rows.map((q) => {
                const alreadyHere = q.product?.id === product.id;
                const isPicked = picked.has(q.id);
                return (
                  <li key={q.id}>
                    <button
                      type="button"
                      disabled={alreadyHere}
                      onClick={() => togglePick(q)}
                      className={`w-full flex items-center gap-4 px-6 py-3 text-left transition-colors ${
                        alreadyHere ? "opacity-50 cursor-not-allowed" : isPicked ? "bg-cyan-50" : "hover:bg-gray-50"
                      }`}
                    >
                      <span className="flex-shrink-0">
                        {isPicked ? <CheckSquare className="w-5 h-5 text-cyan-600" /> : <Square className="w-5 h-5 text-gray-300" />}
                      </span>
                      <div className="flex-1 min-w-0">
                        <QualificationCell q={q} />
                        <div className="text-xs text-gray-500 mt-0.5">{q.institution || "—"}</div>
                      </div>
                      <div className="hidden sm:flex flex-col items-end gap-1 flex-shrink-0">
                        <AreaBadge area={q.studyArea} />
                        {alreadyHere ? (
                          <span className="text-[11px] text-gray-400">Already attached</span>
                        ) : (
                          <ProductPill product={q.product} />
                        )}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-4 text-sm text-gray-500">
            <span>
              <span className="font-medium text-gray-700">{totalElements}</span> found
            </span>
            <span>
              <span className="font-medium text-cyan-700">{picked.size}</span> selected
            </span>
            {picked.size > 0 && (
              <button onClick={() => setPicked(new Map())} className="text-xs text-gray-500 hover:text-red-600 hover:underline">
                Clear
              </button>
            )}
          </div>
          <div className="flex items-center gap-3">
            {totalPages > 1 && (
              <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={(p) => setCurrentPage(p)} showIcons theme={paginationTheme} />
            )}
            <button
              onClick={submit}
              disabled={isSubmitting || picked.size === 0}
              className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-cyan-600 rounded-lg hover:bg-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
              Attach {picked.size > 0 ? `(${picked.size})` : ""}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
